import { ReactNode, useState } from 'react';

import { AutomationTestingProps } from '@/types';
import { getChangeConfirmationPolicyFromCache } from '@/react/portainer/policies/utils/change-confirmation/getChangeConfirmationPolicyFromCache';
import { notifyDialogClosed } from '@/react/portainer/policies/utils/change-confirmation/shouldSkipConfirm';

import { Card } from '@@/primitives/Card';
import { SwitchField } from '@@/form-components/SwitchField';

import { ModalType, type ButtonOptions } from './types';
import { openModal } from './open-modal';
import { OnSubmit } from './Modal/types';
import { Dialog } from './Dialog';
import { buildCancelButton, buildConfirmButton } from './utils';
import { deriveDialogOptions } from './confirm';

function SwitchPrompt({
  onSubmit,
  title,
  confirmButton = buildConfirmButton('OK'),
  switchLabel,
  modalType,
  message,
  defaultValue = false,
  'data-cy': dataCy,
}: {
  onSubmit: OnSubmit<{ value: boolean }>;
  title: string;
  switchLabel: string;
  confirmButton?: ButtonOptions<true>;
  modalType?: ModalType;
  message?: ReactNode;
  defaultValue?: boolean;
} & AutomationTestingProps) {
  const [value, setValue] = useState(defaultValue);

  const confirmPolicy = getChangeConfirmationPolicyFromCache();
  // Pass message:undefined so deriveDialogOptions returns only the policy header
  // (policy text + snooze), without Card-wrapping the message. The component
  // handles the Card itself so the switch field is included inside it.
  const derived = deriveDialogOptions(
    { title, message: undefined, modalType },
    confirmPolicy
  );
  const policyMessage = derived.message || undefined;

  const switchField = (
    <SwitchField
      name="value"
      data-cy={dataCy}
      label={switchLabel}
      checked={value}
      onChange={setValue}
    />
  );

  return (
    <Dialog
      modalType={derived.modalType}
      title={derived.title ?? title}
      message={
        policyMessage ? (
          <>
            <div className="mb-3">{policyMessage}</div>
            <Card.Container variant="filled">
              <Card.Body>
                {message && <p className="text-muted !mb-3">{message}</p>}
                {switchField}
              </Card.Body>
            </Card.Container>
          </>
        ) : (
          <>
            {message && <div className="mb-3">{message}</div>}
            {switchField}
          </>
        )
      }
      onSubmit={(confirm) => onSubmit(confirm ? { value } : undefined)}
      buttons={[buildCancelButton(), confirmButton]}
    />
  );
}

export async function openSwitchPrompt(
  title: string,
  switchLabel: string,
  {
    confirmButton,
    modalType,
    message,
    defaultValue,
    'data-cy': dataCy,
  }: {
    confirmButton?: ButtonOptions<true>;
    modalType?: ModalType;
    message?: ReactNode;
    defaultValue?: boolean;
  } & AutomationTestingProps = {
    'data-cy': 'switch-prompt',
  }
) {
  const result = await openModal(SwitchPrompt, {
    confirmButton,
    title,
    switchLabel,
    modalType,
    message,
    defaultValue,
    'data-cy': dataCy,
  });
  notifyDialogClosed();
  return result;
}
